import {
  TicketApprovalStatusLookUp,
  TicketCategoryLookUp,
  TicketPriorityLookUp,
  TicketTypeLookUp,
  TicketStatusCodeLookUp,
  TableColumnsDisplayNamesLookUp,
  FilterOptions,
  EmptyStateType,
  SortOrder,
} from 'src/components/Tickets/models/Tickets.interface';
import {
  ChatBubblesQuestionRegularIcon,
  ClipboardRegularIcon,
  WarningRegularIcon,
  ApprovedIcon,
  RejectedIcon,
  WaitingIcon,
  CancelledIcon,
  InProgressIcon,
  InvalidIcon,
  OnHoldIcon,
  OpenIcon,
  ResolvedIcon,
  WaitingFor3rdPartyIcon,
} from 'src/components/Tickets/common/styles/icon.styled';

import { createFilterOptionsFactory } from './utils/factory';

export const INITIAL_PAGE_NUMBER = 1;
export const MAX_PAGE_SIZE = 10;

export const tableHeaderDisplayName: TableColumnsDisplayNamesLookUp = {
  createdon: 'Created',
  new_id: 'ID',
  new_description: 'Title',
  new_tickettype: 'Ticket type',
  _new_category_value: 'Category',
  new_priority: 'Priority',
  new_approvalstatus: 'Approval',
  owner: 'Owner',
  statuscode: 'Status',
};

export const sortMobileOptions: Partial<
  Record<
    keyof TableColumnsDisplayNamesLookUp,
    {
      sortSectionLabel: string;
      options: {
        asc: { label: string; value: SortOrder };
        desc: { label: string; value: SortOrder };
      };
    }
  >
> = {
  new_id: {
    sortSectionLabel: 'Ticket ID',
    options: {
      asc: { label: 'Ascending (1-9)', value: 'asc' },
      desc: { label: 'Descending (9-1)', value: 'desc' },
    },
  },
  createdon: {
    sortSectionLabel: 'Created',
    options: {
      asc: { label: 'Earliest first', value: 'asc' },
      desc: { label: 'Latest first', value: 'desc' },
    },
  },
  new_description: {
    sortSectionLabel: 'Title',
    options: {
      asc: { label: 'Ascending (A-Z)', value: 'asc' },
      desc: { label: 'Descending (Z-A)', value: 'desc' },
    },
  },
  _new_category_value: {
    sortSectionLabel: 'Category',
    options: {
      asc: { label: 'Ascending (A-Z)', value: 'asc' },
      desc: { label: 'Descending (Z-A)', value: 'desc' },
    },
  },
  new_priority: {
    sortSectionLabel: 'Priority',
    options: {
      asc: { label: 'Ascending (A-Z)', value: 'asc' },
      desc: { label: 'Descending (Z-A)', value: 'desc' },
    },
  },
  new_tickettype: {
    sortSectionLabel: 'Ticket type',
    options: {
      asc: { label: 'Ascending (A-Z)', value: 'asc' },
      desc: { label: 'Descending (Z-A)', value: 'desc' },
    },
  },
  new_approvalstatus: {
    sortSectionLabel: 'Approval',
    options: {
      asc: { label: 'Ascending (A-Z)', value: 'asc' },
      desc: { label: 'Descending (Z-A)', value: 'desc' },
    },
  },
  owner: {
    sortSectionLabel: 'Owner',
    options: {
      asc: { label: 'Ascending (A-Z)', value: 'asc' },
      desc: { label: 'Descending (Z-A)', value: 'desc' },
    },
  },
  statuscode: {
    sortSectionLabel: 'Status',
    options: {
      asc: { label: 'Ascending (A-Z)', value: 'asc' },
      desc: { label: 'Descending (Z-A)', value: 'desc' },
    },
  },
};

export const emptyTableState: EmptyStateType = {
  noTickets: {
    title: 'There are no tickets to show',
    subtitle: 'Once you’ve submitted a ticket, you’ll find it here.',
  },
  noFilters: {
    title: 'There are no tickets to show',
    subtitle: 'Try refining your filter criteria.',
  },
};

export const ticketTypeLookUp: TicketTypeLookUp = {
  100000000: {
    label: 'Request',
    icon: ChatBubblesQuestionRegularIcon,
  },
  100000001: { label: 'Incident', icon: WarningRegularIcon },
  100000002: { label: 'Internal Task', icon: ClipboardRegularIcon },
};

export const ticketCategoryLookUp: TicketCategoryLookUp = {
  '8c40f26b-01c5-ee11-907a-000d3aba332f': { label: 'Business Trip' },
  '8e40f26b-01c5-ee11-907a-000d3aba332f': { label: 'Courier Service' },
  '9040f26b-01c5-ee11-907a-000d3aba332f': { label: 'External Visitors' },
  '9240f26b-01c5-ee11-907a-000d3aba332f': { label: 'Learning Platforms' },
  '9440f26b-01c5-ee11-907a-000d3aba332f': { label: 'Certifications & Exams' },
  '9640f26b-01c5-ee11-907a-000d3aba332f': { label: 'Employee Leaving' },
  '9840f26b-01c5-ee11-907a-000d3aba332f': { label: 'Event Planning' },
  '9a40f26b-01c5-ee11-907a-000d3aba332f': { label: 'Purchasing' },
  '9c40f26b-01c5-ee11-907a-000d3aba332f': { label: 'Other' },
  '9e40f26b-01c5-ee11-907a-000d3aba332f': { label: 'Access Management' },
  'a040f26b-01c5-ee11-907a-000d3aba332f': { label: 'Project Support' },
  'a240f26b-01c5-ee11-907a-000d3aba332f': { label: 'Hardware Support' },
  'a440f26b-01c5-ee11-907a-000d3aba332f': { label: 'Software Support' },
  'a640f26b-01c5-ee11-907a-000d3aba332f': { label: 'Network Support' },
  'd460c944-01c5-ee11-9079-000d3aba36db': { label: 'Facility Support' },
};

export const ticketPriorityLookUp: TicketPriorityLookUp = {
  100000000: { label: 'High' },
  100000001: { label: 'Medium' },
  100000002: { label: 'Low' },
};

export const ticketApprovalStatusLookUp: TicketApprovalStatusLookUp = {
  100000000: { label: 'Waiting for Approval', icon: WaitingIcon },
  100000001: { label: 'Approved', icon: ApprovedIcon },
  100000002: { label: 'Rejected', icon: RejectedIcon },
};

export const ticketStatusCodeLookUp: TicketStatusCodeLookUp = {
  1: {
    label: 'Open',
    badgeComponentOptions: {
      appearance: 'tint',
      badgeColor: 'brand',
      icon: OpenIcon,
      css: {
        darkMode: {
          labelColor: '#479EF5',
          iconColor: '#479EF5',
          backgroundColor: 'transparent',
          boarderColor: '#479EF5',
        },
        lightMode: {
          labelColor: '#115EA3',
          iconColor: '#115EA3',
          backgroundColor: '#EBF3FC',
          boarderColor: '#B4D6FA',
        },
      },
    },
  },
  100000002: {
    label: 'Waiting for 3rd party',
    badgeComponentOptions: {
      appearance: 'tint',
      badgeColor: 'warning',
      icon: WaitingFor3rdPartyIcon,
      css: {
        darkMode: {
          labelColor: '#FDCFB4',
          iconColor: '#FDCFB4',
          backgroundColor: 'transparent',
          boarderColor: '#FDCFB4',
        },
        lightMode: {
          labelColor: '#8A3707',
          iconColor: '#8A3707',
          backgroundColor: '#FFF9F5',
          boarderColor: '#FDCFB4',
        },
      },
    },
  },
  100000003: {
    label: 'On hold',
    badgeComponentOptions: {
      appearance: 'tint',
      badgeColor: 'warning',
      icon: OnHoldIcon,
      css: {
        darkMode: {
          labelColor: '#FDCFB4',
          iconColor: '#FDCFB4',
          backgroundColor: 'transparent',
          boarderColor: '#FDCFB4',
        },
        lightMode: {
          labelColor: '#8A3707',
          iconColor: '#8A3707',
          backgroundColor: '#FFF9F5',
          boarderColor: '#FDCFB4',
        },
      },
    },
  },
  100000005: {
    label: 'Resolved',
    badgeComponentOptions: {
      appearance: 'tint',
      badgeColor: 'success',
      icon: ResolvedIcon,
      css: {
        darkMode: {
          labelColor: '#9FD89F',
          iconColor: '#9FD89F',
          backgroundColor: 'transparent',
          boarderColor: '#9FD89F',
        },
        lightMode: {
          labelColor: '#0E700E',
          iconColor: '#0E700E',
          backgroundColor: '#F1FAF1',
          boarderColor: '#9FD89F',
        },
      },
    },
  },
  2: {
    label: 'Invalid',
    badgeComponentOptions: {
      appearance: 'tint',
      badgeColor: 'danger',
      icon: InvalidIcon,
      css: {
        darkMode: {
          labelColor: '#E9835E',
          iconColor: '#E9835E',
          backgroundColor: 'transparent',
          boarderColor: '#E9835E',
        },
        lightMode: {
          labelColor: '#C43501',
          iconColor: '#C43501',
          backgroundColor: '#FDF6F3',
          boarderColor: '#F4BFAB',
        },
      },
    },
  },
  100000004: {
    label: 'Cancelled',
    badgeComponentOptions: {
      appearance: 'tint',
      badgeColor: 'informative',
      icon: CancelledIcon,
      css: {
        darkMode: {
          labelColor: '#ADADAD',
          iconColor: '#ADADAD',
          backgroundColor: 'transparent',
          boarderColor: '#525252',
        },
        lightMode: {
          labelColor: '#616161',
          iconColor: '#616161',
          backgroundColor: '#F0F0F0',
          boarderColor: '#E0E0E0',
        },
      },
    },
  },
  100000001: {
    label: 'In progress',
    badgeComponentOptions: {
      appearance: 'tint',
      badgeColor: 'brand',
      icon: InProgressIcon,
      css: {
        darkMode: {
          labelColor: '#479EF5',
          iconColor: '#479EF5',
          backgroundColor: 'transparent',
          boarderColor: '#479EF5',
        },
        lightMode: {
          labelColor: '#115EA3',
          iconColor: '#115EA3',
          backgroundColor: '#EBF3FC',
          boarderColor: '#B4D6FA',
        },
      },
    },
  },
};

export const columnFilterOptions: FilterOptions = {
  new_tickettype: createFilterOptionsFactory(Object.entries(ticketTypeLookUp)),
  _new_category_value: createFilterOptionsFactory(
    Object.entries(ticketCategoryLookUp)
  ),
  new_priority: createFilterOptionsFactory(
    Object.entries(ticketPriorityLookUp)
  ),
  new_approvalstatus: createFilterOptionsFactory(
    Object.entries(ticketApprovalStatusLookUp)
  ),
  statuscode: createFilterOptionsFactory(
    Object.entries(ticketStatusCodeLookUp)
  ),
};
